(() => {
  'use strict';

  const root = window.Shopify?.routes?.root || '/';
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const storage = {
    get(key, fallback = []) { try { return JSON.parse(localStorage.getItem(key)) || fallback; } catch { return fallback; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} }
  };
  let pincodeEddPromise = null;

  function loadPincodeEdd(url) {
    if (window.BBRL_PINCODE_EDD) return Promise.resolve(window.BBRL_PINCODE_EDD);
    if (!url) return Promise.reject(new Error('Pincode EDD asset is missing'));
    if (pincodeEddPromise) return pincodeEddPromise;

    pincodeEddPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.pincodeEddScript = '';
      script.onload = () => {
        if (window.BBRL_PINCODE_EDD) resolve(window.BBRL_PINCODE_EDD);
        else reject(new Error('Pincode EDD data did not initialise'));
      };
      script.onerror = () => reject(new Error('Pincode EDD data failed to load'));
      document.head.appendChild(script);
    }).catch(error => {
      qs('[data-pincode-edd-script]')?.remove();
      pincodeEddPromise = null;
      throw error;
    });

    return pincodeEddPromise;
  }

  function indiaToday() {
    try {
      const parts = Object.fromEntries(
        new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        }).formatToParts(new Date()).filter(part => part.type !== 'literal').map(part => [part.type, part.value])
      );
      return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12);
    } catch (_) {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      return date;
    }
  }

  function ordinalDay(day) {
    const mod100 = day % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${day}th`;
    return `${day}${day % 10 === 1 ? 'st' : day % 10 === 2 ? 'nd' : day % 10 === 3 ? 'rd' : 'th'}`;
  }

  function formatDeliveryRange(eddDays, bufferDays = 2) {
    const start = indiaToday();
    start.setDate(start.getDate() + Math.max(0, Number(eddDays) || 0));
    const end = new Date(start);
    end.setDate(end.getDate() + Math.max(0, Number(bufferDays) || 0));
    const dateText = date => {
      const weekday = date.toLocaleDateString('en-IN', { weekday: 'short' });
      const month = date.toLocaleDateString('en-IN', { month: 'short' });
      return `${weekday}, ${month} ${ordinalDay(date.getDate())}`;
    };
    return `${dateText(start)} to ${dateText(end)}`;
  }

  function money(cents) {
    const currency = window.Shopify?.currency?.active || 'INR';
    try { return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(cents / 100); }
    catch { return `₹${Math.round(cents / 100)}`; }
  }

  function decimalMoney(amount) {
    const value = Number(amount);
    return money(Number.isFinite(value) ? Math.round(value * 100) : 0);
  }

  function escapeHTML(value = '') {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  function itemDiscountAllocations(item = {}) {
    const allocations = [
      ...(Array.isArray(item.discount_allocations) ? item.discount_allocations : []),
      ...(Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [])
    ];
    const seen = new Set();
    return allocations.filter(allocation => {
      const key = `${discountApplicationKey(allocation.discount_application)}::${Number(allocation.amount || 0)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function discountApplicationKey(application = {}) {
    return [application.type || 'automatic', application.target_type || 'line_item', application.title || 'Discount'].join('::').toLowerCase();
  }

  function cartDiscountApplications(cart = {}) {
    if (Array.isArray(cart.discount_applications) && cart.discount_applications.length) {
      return cart.discount_applications.map(application => ({
        application,
        amount: Number(application.total_allocated_amount || 0)
      })).filter(discount => discount.amount > 0);
    }

    const discounts = new Map();
    const addAllocation = (application = {}, amount = 0, replace = false) => {
      const allocatedAmount = Number(amount || 0);
      if (allocatedAmount <= 0) return;
      const key = discountApplicationKey(application);
      const existing = discounts.get(key);
      if (!existing) {
        discounts.set(key, { application, amount: allocatedAmount });
      } else if (replace) {
        existing.amount = Math.max(existing.amount, allocatedAmount);
      } else {
        existing.amount += allocatedAmount;
      }
    };

    (cart.items || []).forEach(item => {
      itemDiscountAllocations(item).forEach(allocation => addAllocation(allocation.discount_application, allocation.amount));
    });
    (cart.cart_level_discount_applications || []).forEach(application => {
      addAllocation(application, application.total_allocated_amount, true);
    });
    return [...discounts.values()];
  }

  function cartItemOptionsMarkup(item = {}) {
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    if (options.length) {
      return `<div class="cart-item__options">${options.map(option => `<span><strong>${escapeHTML(option.name || 'Option')}:</strong> ${escapeHTML(option.value || '')}</span>`).join('')}</div>`;
    }
    if (item.variant_title && item.variant_title !== 'Default Title') {
      return `<div class="cart-item__options"><span>${escapeHTML(item.variant_title)}</span></div>`;
    }
    return '';
  }

  function toast(message) {
    const element = qs('[data-toast]');
    if (!element) return;
    element.textContent = message;
    element.hidden = false;
    clearTimeout(toast.timer);
    toast.timer = setTimeout(() => { element.hidden = true; }, 2600);
  }

  function wishlistNotice(message, options = {}) {
    const celebrate = options.celebrate === true;
    const subtle = options.subtle === true;
    qsa('[data-wishlist-notice]').forEach(notice => notice.remove());
    const notice = document.createElement('div');
    notice.className = `wishlist-notice${celebrate ? ' wishlist-notice--celebrate' : ''}${subtle ? ' wishlist-notice--subtle' : ''}`;
    notice.dataset.wishlistNotice = '';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.innerHTML = `<span class="wishlist-notice__text"></span>${celebrate ? `<span class="wishlist-notice__burst" aria-hidden="true">${Array.from({ length: 10 }, (_, index) => `<i style="--burst-angle:${index * 36}deg;--burst-delay:${index % 3 * 30}ms"></i>`).join('')}</span>` : ''}`;
    qs('.wishlist-notice__text', notice).textContent = message;
    document.body.appendChild(notice);
    setTimeout(() => notice.remove(), celebrate ? 2200 : 1900);
  }

  function track(event, ecommerce = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ecommerce });
  }

  function initPageLoader() {
    const loader = qs('[data-page-loader]');
    if (!loader) return;
    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      loader.classList.add('is-hidden');
      document.body.classList.remove('has-page-loader');
      loader.addEventListener('transitionend', () => loader.remove(), { once: true });
      setTimeout(() => loader.remove(), 700);
    };
    if (document.readyState === 'complete') requestAnimationFrame(dismiss);
    else window.addEventListener('load', dismiss, { once: true });
    setTimeout(dismiss, Math.max(2000, Number(loader.dataset.loaderTimeout || 6000)));
  }

  class Drawers {
    constructor() {
      this.overlay = qs('[data-drawer-overlay]');
      this.active = null;
      this.returnFocus = null;
      document.addEventListener('click', event => {
        const open = event.target.closest('[data-drawer-open]');
        if (open) { event.preventDefault(); this.open(open.dataset.drawerOpen, open); }
        if (event.target.closest('[data-drawer-close]')) this.close();
        if (event.target.closest('[data-cart-open]')) {
          event.preventDefault();
          const trigger = event.target.closest('[data-cart-open]');
          this.open('cart-drawer', trigger);
          Cart.refresh();
        }
      });
      this.overlay?.addEventListener('click', () => this.close());
      document.addEventListener('keydown', event => { if (event.key === 'Escape') this.close(); });
    }
    open(id, trigger) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      if (this.active && this.active !== drawer) {
        this.active.classList.remove('is-open');
        this.active.setAttribute('aria-hidden', 'true');
      }
      this.active = drawer;
      this.returnFocus = trigger || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      if (this.overlay) this.overlay.hidden = false;
      requestAnimationFrame(() => qs('button, a, input, select', drawer)?.focus({ preventScroll: true }));
      if (id === 'wishlist-drawer') Wishlist.render();
    }
    close() {
      if (!this.active) return;
      this.active.classList.remove('is-open');
      this.active.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      if (this.overlay) this.overlay.hidden = true;
      this.returnFocus?.focus?.({ preventScroll: true });
      this.active = null;
    }
  }

  const Cart = {
    celebrationStorageKey: 'bluberyl:cart-celebrations',
    couponCelebrationStorageKey: 'bluberyl:coupon-celebration-shown',
    celebrationNextAt: 0,
    currentCart: null,
    cartLoadPromise: null,
    celebrationState() {
      try { return new Set(JSON.parse(sessionStorage.getItem(this.celebrationStorageKey)) || []); }
      catch (_) { return new Set(); }
    },
    saveCelebrationState(state) {
      try { sessionStorage.setItem(this.celebrationStorageKey, JSON.stringify([...state])); } catch (_) {}
    },
    syncCelebrations(scope = document, shouldCelebrate = true) {
      const milestones = qsa('[data-cart-celebration]', scope);
      const state = this.celebrationState();
      const presentIds = new Set(milestones.map(element => element.dataset.celebrationId).filter(Boolean));
      const messages = [];
      const enabled = qs('[data-cart-growth-tools]', scope)?.dataset.celebrationsEnabled !== 'false';

      [...state].forEach(id => { if (!presentIds.has(id)) state.delete(id); });
      milestones.forEach(element => {
        const id = element.dataset.celebrationId;
        if (!id) return;
        const reached = element.dataset.reached === 'true';
        if (!reached) {
          state.delete(id);
          return;
        }
        if (!state.has(id) && shouldCelebrate && enabled) {
          messages.push(element.dataset.celebrationMessage || 'Offer unlocked!');
        }
        state.add(id);
      });
      this.saveCelebrationState(state);
      messages.forEach(message => this.celebrate(message));
    },
    celebrate(message, options = {}) {
      const small = options.small === true;
      const now = Date.now();
      const showAt = Math.max(now, this.celebrationNextAt);
      this.celebrationNextAt = showAt + (small ? 700 : 1050);
      setTimeout(() => {
        const colors = ['rgb(var(--color-accent))', 'rgb(var(--color-accent-2))', 'rgb(var(--color-ink))', '#ffd447'];
        const particleCount = small ? 10 : 28;
        const particles = Array.from({ length: particleCount }, (_, index) => `<i style="--angle:${index * (360 / particleCount)}deg;--distance:${small ? 34 + index % 3 * 7 : 72 + index % 5 * 12}px;--delay:${index % 4 * 35}ms;--particle-color:${colors[index % colors.length]}"></i>`).join('');
        const blast = document.createElement('div');
        blast.className = `cart-celebration-blast${small ? ' cart-celebration-blast--small' : ''}`;
        blast.setAttribute('role', 'status');
        blast.setAttribute('aria-live', 'polite');
        blast.innerHTML = `<div class="cart-celebration-blast__particles" aria-hidden="true">${particles}</div><div class="cart-celebration-blast__message"><span aria-hidden="true">✦</span><strong>${escapeHTML(message)}</strong></div>`;
        document.body.appendChild(blast);
        requestAnimationFrame(() => blast.classList.add('is-active'));
        setTimeout(() => blast.remove(), small ? 1350 : 1900);
      }, Math.max(0, showAt - now));
    },
    celebrateAutomaticCoupon(cartBefore = {}, cartAfter = {}) {
      try { if (sessionStorage.getItem(this.couponCelebrationStorageKey) === 'true') return; } catch (_) {}
      const beforeDiscounts = new Map(
        cartDiscountApplications(cartBefore)
          .filter(({ application }) => application?.type !== 'discount_code')
          .map(({ application, amount }) => [discountApplicationKey(application), Number(amount || 0)])
      );
      const activated = cartDiscountApplications(cartAfter).find(({ application, amount }) => {
        if (application?.type === 'discount_code' || Number(amount || 0) <= 0) return false;
        return Number(beforeDiscounts.get(discountApplicationKey(application)) || 0) <= 0;
      });
      if (!activated) return;
      try { sessionStorage.setItem(this.couponCelebrationStorageKey, 'true'); } catch (_) {}
      const title = activated.application?.title || 'Offer';
      this.celebrate(`${title} activated — you saved ${money(activated.amount)}.`, { small: true });
    },
    duplicateSize(item = {}, fallback = '') {
      const sizeOption = (item.options_with_values || []).find(option => String(option.name || '').toLowerCase() === 'size');
      if (sizeOption?.value) return String(sizeOption.value);
      if (fallback) return String(fallback);
      return item.variant_title && item.variant_title !== 'Default Title' ? String(item.variant_title) : '';
    },
    confirmDuplicate(item, size = '') {
      return new Promise(resolve => {
        const dialog = document.createElement('dialog');
        const resolvedSize = this.duplicateSize(item, size);
        dialog.className = 'cart-duplicate-dialog';
        dialog.setAttribute('aria-labelledby', 'CartDuplicateTitle');
        dialog.innerHTML = `<div class="cart-duplicate-dialog__card"><span class="cart-duplicate-dialog__icon" aria-hidden="true">?</span><h2 id="CartDuplicateTitle">Already in your cart</h2><p>Product already added to cart${resolvedSize ? ` in <strong>${escapeHTML(resolvedSize)}</strong> size` : ''}, do you want to add again?</p><div><button class="button button--secondary" type="button" data-duplicate-no>No</button><button class="button button--ink" type="button" data-duplicate-yes>Yes</button></div></div>`;
        document.body.appendChild(dialog);
        let settled = false;
        const finish = answer => {
          if (settled) return;
          settled = true;
          dialog.close?.();
          dialog.remove();
          resolve(answer);
        };
        qs('[data-duplicate-no]', dialog)?.addEventListener('click', () => finish(false));
        qs('[data-duplicate-yes]', dialog)?.addEventListener('click', () => finish(true));
        dialog.addEventListener('cancel', event => { event.preventDefault(); finish(false); });
        dialog.addEventListener('click', event => { if (event.target === dialog) finish(false); });
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    },
    async get() {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Unable to load cart');
      return response.json();
    },
    async getCached() {
      if (this.currentCart) return this.currentCart;
      if (!this.cartLoadPromise) {
        this.cartLoadPromise = this.get()
          .then(cart => {
            this.currentCart = cart;
            return cart;
          })
          .finally(() => { this.cartLoadPromise = null; });
      }
      return this.cartLoadPromise;
    },
    async getDrawerSection() {
      const response = await fetch(`${root}?section_id=cart-drawer`, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Unable to refresh cart prices');
      return response.text();
    },
    renderDrawerSection(markup) {
      if (!markup) return;
      const documentFragment = new DOMParser().parseFromString(markup, 'text/html');
      const updatedDrawer = qs('[data-cart-drawer]', documentFragment);
      const currentDrawer = qs('[data-cart-drawer]');
      if (!updatedDrawer || !currentDrawer) return;
      currentDrawer.innerHTML = updatedDrawer.innerHTML;
      this.syncCelebrations(currentDrawer, true);
    },
    async add(id, quantity = 1, options = {}) {
      const openDrawer = options.openDrawer !== false;
      const variantID = Number(id);
      const cartBefore = await this.getCached().catch(() => ({ items: [], discount_applications: [] }));
      const existingItem = (cartBefore.items || []).find(item => Number(item.variant_id || item.id) === variantID);
      if (options.confirmDuplicate === true && existingItem) {
        const confirmed = await this.confirmDuplicate(existingItem, options.size || '');
        if (!confirmed) {
          document.dispatchEvent(new CustomEvent('cart:add-cancelled', { detail: { variantId: variantID, cart: cartBefore } }));
          return { cancelled: true, cart: cartBefore, item: existingItem };
        }
      }
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantID, quantity: Number(quantity) }] })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'This size could not be added.');
      track('add_to_cart', { items: data.items?.map(item => ({ item_id: item.product_id, item_variant: item.variant_title, item_name: item.product_title, price: item.price / 100, quantity: item.quantity })) });

      // Refresh the lightweight cart JSON first so the customer sees an immediate response.
      // The heavier cart-drawer section (MRP, discounts and offer messaging) is updated just after.
      const cartAfter = await this.get();
      this.render(cartAfter);
      if (openDrawer) window.themeDrawers?.open('cart-drawer');
      this.getDrawerSection()
        .then(markup => this.renderDrawerSection(markup))
        .catch(() => {});

      this.celebrate('Added to cart', { small: true });
      this.celebrateAutomaticCoupon(cartBefore, cartAfter);
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { variantId: variantID, cart: cartAfter, item: data.items?.[0] || data } }));
      return { cancelled: false, cart: cartAfter, item: data.items?.[0] || data };
    },
    async change(key, quantity) {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) })
      });
      if (!response.ok) throw new Error('Could not update the bag.');
      const cart = await response.json();
      this.render(cart);
      try { this.renderDrawerSection(await this.getDrawerSection()); } catch (_) {}
      return cart;
    },
    async refresh() {
      try {
        const [cart, drawerMarkup] = await Promise.all([
          this.get(),
          this.getDrawerSection().catch(() => '')
        ]);
        this.render(cart);
        this.renderDrawerSection(drawerMarkup);
        return cart;
      }
      catch (error) { toast(error.message); }
    },
    render(cart) {
      this.currentCart = cart;
      qsa('[data-cart-count]').forEach(element => { element.textContent = cart.item_count; element.hidden = cart.item_count === 0; });
      const titleCount = qs('[data-cart-title-count]');
      if (titleCount) titleCount.textContent = `(${cart.item_count})`;
      const body = qs('[data-cart-body]');
      const footer = qs('[data-cart-footer]');
      if (!body || !footer) return;
      const priceMetadata = new Map(
        qsa('[data-cart-line][data-cart-pricing-resolved="true"]', body).map(element => [
          Number(element.dataset.cartVariantId),
          {
            compareAtPrice: Number(element.dataset.cartCompareAtPrice || 0),
            sellingPrice: Number(element.dataset.cartSellingPrice || 0)
          }
        ])
      );
      footer.hidden = cart.item_count === 0;
      if (!cart.item_count) {
        body.innerHTML = `<div class="cart-empty"><span class="cart-empty__icon"><svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 8h14l-1 13H6L5 8Z"/><path d="M9 9V6a3 3 0 0 1 6 0v3"/></svg></span><h2>Your bag is waiting for a good print.</h2><a class="button button--primary" href="${root}collections/all">Shop all</a></div>`;
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        return;
      }
      body.innerHTML = `<div class="cart-items">${cart.items.map(item => {
        const variantID = Number(item.variant_id || item.id);
        const resolvedPrice = priceMetadata.get(variantID);
        const sellingUnitPrice = Number(resolvedPrice?.sellingPrice || item.original_price || item.price || 0);
        const compareAtPrice = Number(resolvedPrice?.compareAtPrice || 0);
        const originalLinePrice = Number(item.original_line_price || sellingUnitPrice * Number(item.quantity || 0));
        const apiFinalLinePrice = Number(item.final_line_price);
        const compareAtLinePrice = compareAtPrice * Number(item.quantity || 0);
        const lineDiscounts = itemDiscountAllocations(item);
        const allocatedDiscount = lineDiscounts.reduce((total, allocation) => total + Number(allocation.amount || 0), 0);
        const allocatedFinalLinePrice = Math.max(0, originalLinePrice - allocatedDiscount);
        const finalLinePrice = Number.isFinite(apiFinalLinePrice)
          ? Math.min(apiFinalLinePrice, allocatedFinalLinePrice)
          : allocatedFinalLinePrice;
        const appliedOfferMarkup = lineDiscounts
          .filter(allocation => Number(allocation.amount || 0) > 0 && allocation.discount_application?.title)
          .map(allocation => {
            const application = allocation.discount_application || {};
            const title = escapeHTML(application.title);
            const label = application.type === 'discount_code' ? 'Coupon' : 'Offer';
            return `<span class="cart-item__coupon" aria-label="${title} applied">${label}: ${title}</span>`;
          })
          .join('');
        const priceMarkup = compareAtPrice > sellingUnitPrice
          ? `<div class="cart-item__price"><s>${money(compareAtLinePrice)}</s><strong>${money(finalLinePrice)}</strong>${appliedOfferMarkup}</div>`
          : `<div class="cart-item__price"><strong>${money(finalLinePrice)}</strong>${appliedOfferMarkup}</div>`;

        return `
          <article class="cart-item" data-cart-line="${escapeHTML(item.key)}" data-cart-variant-id="${variantID}" data-cart-compare-at-price="${compareAtPrice}" data-cart-selling-price="${sellingUnitPrice}" data-cart-pricing-resolved="${resolvedPrice ? 'true' : 'false'}">
            <a href="${item.url}" class="cart-item__image">${item.image ? `<img src="${item.image}&width=240" alt="${escapeHTML(item.product_title)}" width="120" height="150" loading="lazy">` : ''}</a>
            <div class="cart-item__details"><a href="${item.url}" class="cart-item__title">${escapeHTML(item.product_title)}</a>${cartItemOptionsMarkup(item)}${priceMarkup}
              <div class="cart-item__actions"><div class="quantity"><button type="button" data-cart-quantity="${item.quantity - 1}" aria-label="Decrease quantity"><svg viewBox="0 0 24 24"><path d="M4 12h16"/></svg></button><span>${item.quantity}</span><button type="button" data-cart-quantity="${item.quantity + 1}" aria-label="Increase quantity"><svg viewBox="0 0 24 24"><path d="M12 4v16M4 12h16"/></svg></button></div><button class="text-button" type="button" data-cart-remove>Remove</button></div>
            </div>
          </article>`;
      }).join('')}</div>`;
      const total = qs('[data-cart-total]');
      if (total) total.textContent = money(cart.total_price);
      const originalTotal = qs('[data-cart-original-total]');
      if (originalTotal) {
        const hasDiscountedTotal = Number(cart.original_total_price || 0) > Number(cart.total_price || 0);
        originalTotal.textContent = money(cart.original_total_price || cart.total_price);
        originalTotal.hidden = !hasDiscountedTotal;
      }
      const cartDiscounts = qs('[data-cart-discounts]');
      if (cartDiscounts) {
        const discounts = cartDiscountApplications(cart);
        cartDiscounts.hidden = discounts.length === 0;
        cartDiscounts.innerHTML = discounts.map(({ application, amount }) => `<div class="cart-discount"><span>${application.type === 'discount_code' ? 'Coupon' : 'Discount'}: ${escapeHTML(application.title || 'Discount')}</span><strong>−${money(amount)}</strong></div>`).join('');
      }
      const progress = qs('[data-shipping-threshold]');
      if (progress) {
        const threshold = Number(progress.dataset.shippingThreshold);
        const remaining = Math.max(0, threshold - cart.total_price);
        const message = qs('[data-shipping-message]', progress);
        const bar = qs('[data-shipping-bar]', progress);
        if (message) message.textContent = remaining ? `Add ${money(remaining)} for free shipping.` : 'You unlocked free shipping.';
        if (bar) bar.style.width = `${Math.min(100, cart.total_price / threshold * 100)}%`;
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }
  };

  const QuickAddSheet = {
    dialog: null,
    isOpen: false,
    returnFocus: null,
    shouldRestoreFocus: true,
    sizeGuideCache: new Map(),
    ensure() {
      if (this.dialog?.isConnected) return this.dialog;

      // A fixed overlay is more reliable than native <dialog> on mobile Safari
      // and embedded browsers, where the backdrop could appear without the sheet.
      const dialog = document.createElement('div');
      dialog.id = 'QuickAddSheet';
      dialog.className = 'quick-add-sheet';
      dialog.dataset.quickAddSheet = '';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'QuickAddSheetTitle');
      dialog.hidden = true;
      dialog.innerHTML = '<div data-quick-add-sheet-mount></div>';
      document.body.appendChild(dialog);
      dialog.addEventListener('click', event => {
        if (event.target === dialog) this.close();
      });
      dialog.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        }
      });
      this.dialog = dialog;
      return dialog;
    },
    open(trigger) {
      const cardQuickAdd = trigger.closest('[data-card-quick-add]');
      const template = qs('[data-card-quick-add-template]', cardQuickAdd);
      if (!template) return;
      const dialog = this.ensure();
      if (this.isOpen) this.close({ restoreFocus: false });
      this.returnFocus = trigger;
      const mount = qs('[data-quick-add-sheet-mount]', dialog);
      mount.replaceChildren(template.content.cloneNode(true));
      dialog.hidden = false;
      document.body.classList.add('quick-add-sheet-open');
      this.isOpen = true;
      requestAnimationFrame(() => {
        dialog.classList.add('is-open');
        qs('[data-card-quick-add-close]', dialog)?.focus({ preventScroll: true });
      });
      this.initGallery(mount);
    },
    close(options = {}) {
      const dialog = this.dialog;
      if (!dialog || !this.isOpen) return;
      this.shouldRestoreFocus = options.restoreFocus !== false;
      dialog.classList.remove('is-open');
      dialog.hidden = true;
      document.body.classList.remove('quick-add-sheet-open');
      qs('[data-quick-add-sheet-mount]', dialog)?.replaceChildren();
      this.isOpen = false;
      if (this.shouldRestoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
      this.shouldRestoreFocus = true;
    },
    initGallery(scope) {
      const track = qs('[data-quick-add-gallery-track]', scope);
      const slides = qsa('[data-quick-add-gallery-slide]', scope);
      const thumbnails = qsa('[data-quick-add-gallery-dot]', scope);
      if (!track || slides.length < 2) return;
      const update = () => {
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / Math.max(1, track.clientWidth))));
        thumbnails.forEach((thumbnail, thumbnailIndex) => {
          const active = thumbnailIndex === index;
          thumbnail.classList.toggle('is-active', active);
          thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };
      thumbnails.forEach(thumbnail => {
        thumbnail.addEventListener('click', () => {
          const index = Number(thumbnail.dataset.quickAddGalleryDot || 0);
          track.scrollTo({ left: track.clientWidth * index, behavior: 'smooth' });
        });
      });
      track.addEventListener('scroll', update, { passive: true });
      update();
    },
    choose(button) {
      const panel = button.closest('[data-quick-add-sheet-panel]');
      qsa('[data-card-quick-add-choice]', panel).forEach(choice => {
        const selected = choice === button;
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', String(selected));
      });
      const submit = qs('[data-card-quick-add-submit]', panel);
      if (submit) {
        submit.dataset.cardQuickAddVariant = button.dataset.cardQuickAddVariant || '';
        submit.dataset.quickAddSize = button.dataset.quickAddSize || '';
        submit.disabled = !submit.dataset.cardQuickAddVariant;
      }
      const error = qs('[data-card-quick-add-error]', panel);
      if (error) error.hidden = true;
    },
    async sizeGuideMarkup(productURL) {
      if (this.sizeGuideCache.has(productURL)) return this.sizeGuideCache.get(productURL);
      const request = fetch(productURL, { headers: { Accept: 'text/html' } }).then(async response => {
        if (!response.ok) throw new Error('Unable to load the size chart.');
        const html = await response.text();
        const productDocument = new DOMParser().parseFromString(html, 'text/html');
        const sourceBody = qs('[data-size-guide-modal] .modal__body', productDocument);
        if (!sourceBody) throw new Error('Add a fallback size-chart image in the product-page variant picker.');
        return sourceBody.innerHTML;
      });
      this.sizeGuideCache.set(productURL, request);
      try {
        return await request;
      } catch (error) {
        this.sizeGuideCache.delete(productURL);
        throw error;
      }
    },
    bindSizeRecommenders(scope, panel) {
      qsa('.size-recommender', scope).forEach(recommender => {
        if (recommender.dataset.quickAddBound === 'true') return;
        recommender.dataset.quickAddBound = 'true';
        const input = qs('[data-size-input]', recommender);
        const result = qs('[data-size-result]', recommender);
        const checkButton = qs('[data-size-check]', recommender);
        let rawTable = '';
        try { rawTable = JSON.parse(qs('[data-size-map]', recommender)?.textContent || '""'); } catch (_) {}
        const rows = String(rawTable).split(/\r?\n|;/).map(line => {
          const match = line.trim().match(/^(\d+(?:\.\d+)?)\s*(?:[-–—]\s*(\d+(?:\.\d+)?))?\s*(?::|=|\|)\s*(.+)$/);
          if (!match) return null;
          return { min: Number(match[1]), max: Number(match[2] || match[1]), size: match[3].trim() };
        }).filter(Boolean);
        const recommend = () => {
          if (!input || !result) return;
          result.replaceChildren();
          result.classList.remove('is-success', 'is-error');
          if (!input.value || !input.checkValidity()) {
            result.hidden = false;
            result.classList.add('is-error');
            result.textContent = 'Enter your size to check';
            input.focus({ preventScroll: true });
            return;
          }
          const measurement = Number(input.value);
          const recommendation = rows.find(row => measurement >= Math.min(row.min, row.max) && measurement <= Math.max(row.min, row.max));
          result.hidden = false;
          if (!recommendation) {
            result.classList.add('is-error');
            result.textContent = 'That measurement is outside this product’s size table. Please check the size chart.';
            return;
          }
          result.classList.add('is-success');
          const badge = document.createElement('span');
          badge.className = 'size-recommender__result-badge';
          badge.setAttribute('aria-hidden', 'true');
          badge.textContent = '✓';
          const copy = document.createElement('span');
          copy.className = 'size-recommender__result-copy';
          const kicker = document.createElement('span');
          kicker.className = 'size-recommender__result-kicker';
          kicker.textContent = recommender.dataset.resultKicker || 'Best size found';
          const resultLine = document.createElement('span');
          resultLine.className = 'size-recommender__result-line';
          resultLine.textContent = `${recommender.dataset.resultPrefix || 'We recommend'}: `;
          const strong = document.createElement('strong');
          strong.textContent = recommendation.size;
          resultLine.appendChild(strong);
          copy.append(kicker, resultLine);
          result.append(badge, copy);
          const sizeChoice = qsa('[data-card-quick-add-choice]', panel).find(choice => (
            choice.dataset.quickAddSize?.trim().toLowerCase() === recommendation.size.toLowerCase()
          ));
          if (sizeChoice && !sizeChoice.disabled) {
            const addRecommendedSize = document.createElement('button');
            addRecommendedSize.type = 'button';
            addRecommendedSize.className = 'size-recommender__select';
            addRecommendedSize.textContent = `Add ${recommendation.size} size to cart`;
            addRecommendedSize.addEventListener('click', async () => {
              this.choose(sizeChoice);
              const guide = qs('[data-quick-add-size-guide-panel]', panel);
              if (guide) guide.hidden = true;
              const submit = qs('[data-card-quick-add-submit]', panel);
              if (submit) await this.submit(submit);
            });
            result.appendChild(addRecommendedSize);
          } else {
            const unavailable = document.createElement('small');
            unavailable.textContent = 'This recommended size is currently unavailable.';
            result.appendChild(unavailable);
          }
        };
        checkButton?.addEventListener('click', recommend);
        input?.addEventListener('keydown', event => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          recommend();
        });
      });
    },
    async showSizeGuide(button) {
      const panel = button.closest('[data-quick-add-sheet-panel]');
      const guide = qs('[data-quick-add-size-guide-panel]', panel);
      if (!guide) return;
      guide.hidden = false;
      qs('[data-quick-add-size-guide-close]', guide)?.focus({ preventScroll: true });
      if (guide.dataset.hydrated === 'true' || guide.dataset.loading === 'true') return;
      const content = qs('[data-quick-add-size-guide-content]', guide);
      if (!content) return;
      const fallbackMarkup = content.innerHTML;
      guide.dataset.loading = 'true';
      if (qs('.size-guide-modal__image', content)) {
        content.insertAdjacentHTML('beforeend', '<div class="quick-add-sheet__guide-loading">Loading Find my size…</div>');
      } else {
        content.innerHTML = '<div class="quick-add-sheet__guide-loading">Loading size chart and Find my size…</div>';
      }
      try {
        content.innerHTML = await this.sizeGuideMarkup(guide.dataset.productUrl);
        qsa('script:not([data-size-map])', content).forEach(script => script.remove());
        this.bindSizeRecommenders(content, panel);
        guide.dataset.hydrated = 'true';
      } catch (loadError) {
        content.innerHTML = fallbackMarkup;
        const error = document.createElement('p');
        error.className = 'quick-add-sheet__guide-error';
        error.textContent = loadError.message;
        content.appendChild(error);
      } finally {
        delete guide.dataset.loading;
      }
    },
    hideSizeGuide(button) {
      const panel = button.closest('[data-quick-add-sheet-panel]');
      const guide = qs('[data-quick-add-size-guide-panel]', panel);
      if (guide) guide.hidden = true;
      qs('[data-quick-add-size-guide]', panel)?.focus({ preventScroll: true });
    },
    async submit(button) {
      const panel = button.closest('[data-quick-add-sheet-panel]');
      const variantID = button.dataset.cardQuickAddVariant;
      const error = qs('[data-card-quick-add-error]', panel);
      if (!variantID) {
        if (error) error.hidden = false;
        return;
      }
      const originalText = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
      try {
        const result = await Cart.add(variantID, 1, {
          openDrawer: false,
          confirmDuplicate: true,
          size: button.dataset.quickAddSize || ''
        });
        if (result?.cancelled) return;
        this.close({ restoreFocus: false });
        window.themeDrawers?.open('cart-drawer');
      } catch (submitError) {
        if (error) {
          error.textContent = submitError.message || 'This size could not be added.';
          error.hidden = false;
        } else {
          toast(submitError.message);
        }
      } finally {
        if (button.isConnected) {
          button.removeAttribute('aria-busy');
          button.textContent = originalText;
          button.disabled = !button.dataset.cardQuickAddVariant;
        }
      }
    }
  };

  const Wishlist = {
    key: 'bluberyl:wishlist',
    productCache: new Map(),
    sizeGuideCache: new Map(),
    items() { return storage.get(this.key, []); },
    updateButtons() {
      const items = this.items();
      const handles = new Set(items.map(item => item.handle));
      let enriched = false;
      qsa('[data-wishlist-toggle]').forEach(button => {
        const saved = handles.has(button.dataset.handle);
        button.classList.toggle('is-saved', saved);
        button.setAttribute('aria-pressed', String(saved));
        if (saved && button.dataset.sizeGuideUrl) {
          const item = items.find(entry => entry.handle === button.dataset.handle);
          if (item && !item.sizeGuideUrl) { item.sizeGuideUrl = button.dataset.sizeGuideUrl; enriched = true; }
        }
      });
      if (enriched) storage.set(this.key, items);
      qsa('[data-wishlist-count]').forEach(count => { count.textContent = handles.size; count.hidden = !handles.size; });
      qsa('[data-product-floating-wishlist]').forEach(button => { button.hidden = handles.size < 1; });
    },
    toggle(button) {
      const items = this.items();
      const index = items.findIndex(item => item.handle === button.dataset.handle);
      if (index >= 0) { items.splice(index, 1); wishlistNotice('Removed from wishlist', { subtle: true }); }
      else { items.unshift({ handle: button.dataset.handle, title: button.dataset.title, url: button.dataset.url, image: button.dataset.image, sizeGuideUrl: button.dataset.sizeGuideUrl || '' }); wishlistNotice('item added to wishlist ♥', { subtle: true }); }
      storage.set(this.key, items.slice(0, 40));
      this.updateButtons();
      this.render();
    },
    render() {
      const body = qs('[data-wishlist-body]');
      if (!body) return;
      const items = this.items();
      if (!items.length) { body.innerHTML = '<div class="cart-empty"><h2>Save the prints you like.</h2><p>Your wishlist stays on this device.</p></div>'; return; }
      body.innerHTML = items.map(item => `<article class="wishlist-item" data-wishlist-item="${escapeHTML(item.handle)}"><a href="${item.url}"><img src="${item.image}" alt="${escapeHTML(item.title)}" width="80" height="100" loading="lazy"></a><div class="wishlist-item__details"><a href="${item.url}"><strong>${escapeHTML(item.title)}</strong></a><div class="wishlist-item__quick-add" data-wishlist-quick-add><span class="wishlist-item__loading">Loading sizes…</span></div></div><button type="button" class="icon-button" data-wishlist-remove="${escapeHTML(item.handle)}" aria-label="Remove ${escapeHTML(item.title)}"><svg viewBox="0 0 24 24"><path d="m5 5 14 14M19 5 5 19"/></svg></button></article>`).join('');
      if (body.closest('.wishlist-drawer')?.classList.contains('is-open')) {
        items.forEach(item => this.hydrateQuickAdd(item, body));
      }
    },
    async product(handle) {
      if (!this.productCache.has(handle)) {
        this.productCache.set(handle, fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } }).then(response => {
          if (!response.ok) throw new Error('Product unavailable');
          return response.json();
        }).catch(error => {
          this.productCache.delete(handle);
          throw error;
        }));
      }
      return this.productCache.get(handle);
    },
    async productSizeGuide(item) {
      if (item.sizeGuideUrl) return item.sizeGuideUrl;
      if (!this.sizeGuideCache.has(item.handle)) {
        this.sizeGuideCache.set(item.handle, fetch(item.url, { headers: { Accept: 'text/html' } }).then(async response => {
          if (!response.ok) return '';
          const markup = await response.text();
          const productDocument = new DOMParser().parseFromString(markup, 'text/html');
          return qs('[data-product-section]', productDocument)?.dataset.sizeGuideUrl || '';
        }).catch(() => ''));
      }
      const url = await this.sizeGuideCache.get(item.handle);
      if (url) {
        item.sizeGuideUrl = url;
        const items = this.items();
        const storedItem = items.find(entry => entry.handle === item.handle);
        if (storedItem) { storedItem.sizeGuideUrl = url; storage.set(this.key, items); }
      }
      return url;
    },
    async hydrateQuickAdd(item, body) {
      const card = qsa('[data-wishlist-item]', body).find(element => element.dataset.wishlistItem === item.handle);
      const quickAdd = qs('[data-wishlist-quick-add]', card);
      if (!quickAdd) return;

      try {
        const product = await this.product(item.handle);
        if (!document.contains(card)) return;
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const optionNames = (product.options || []).map(option => typeof option === 'string' ? option : option.name);
        const sizeIndex = optionNames.findIndex(name => String(name).toLowerCase() === 'size');
        const drawer = body.closest('.wishlist-drawer');
        const showSizeGuide = drawer?.dataset.showSizeGuide === 'true';
        let productSizeGuideUrl = item.sizeGuideUrl || '';
        if (showSizeGuide && sizeIndex >= 0 && !productSizeGuideUrl) productSizeGuideUrl = await this.productSizeGuide(item);
        if (!document.contains(card)) return;
        const sizeGuideUrl = productSizeGuideUrl || drawer?.dataset.fallbackSizeGuideUrl || '';
        const sizeGuideLabel = drawer?.dataset.sizeGuideLabel || 'Size guide';
        const hasOtherChoices = (product.options || []).some((option, index) => {
          if (index === sizeIndex) return false;
          const values = typeof option === 'string'
            ? [...new Set(variants.map(variant => variant.options?.[index]).filter(Boolean))]
            : (option.values || []);
          return values.length > 1;
        });
        let choices = [];
        let selectorLabel = sizeIndex >= 0 ? 'Size' : 'Option';

        if (sizeIndex >= 0 && !hasOtherChoices) {
          const values = product.options[sizeIndex]?.values || variants.map(variant => variant.options?.[sizeIndex]);
          [...new Set(values.filter(Boolean))].forEach(value => {
            const matching = variants.filter(variant => variant.options?.[sizeIndex] === value);
            const variant = matching.find(candidate => candidate.available) || matching[0];
            choices.push({ label: value, id: variant?.id, available: Boolean(variant?.available) });
          });
        } else {
          if (hasOtherChoices) selectorLabel = 'Variant';
          choices = variants.map(variant => ({
            label: variants.length === 1 ? 'Default' : (variant.public_title || variant.title || 'Option'),
            id: variant.id,
            available: Boolean(variant.available)
          }));
        }

        const availableChoices = choices.filter(choice => choice.available);
        if (!availableChoices.length) {
          quickAdd.innerHTML = '<button class="wishlist-item__add" type="button" disabled>Sold out</button>';
          return;
        }

        const needsChoice = choices.length > 1 || sizeIndex >= 0;
        const selectorID = `WishlistVariant-${item.handle}`;
        const sizeGuide = showSizeGuide && sizeIndex >= 0 && sizeGuideUrl
          ? `<a class="wishlist-item__size-guide" href="${escapeHTML(sizeGuideUrl)}">${escapeHTML(sizeGuideLabel)}</a>`
          : '';
        const select = needsChoice
          ? `<div class="wishlist-item__size"><div class="wishlist-item__size-heading"><label for="${escapeHTML(selectorID)}">${selectorLabel}</label>${sizeGuide}</div><select id="${escapeHTML(selectorID)}" data-wishlist-variant aria-label="Select ${selectorLabel.toLowerCase()}"><option value="">Choose ${selectorLabel.toLowerCase()}</option>${choices.map(choice => `<option value="${choice.id || ''}"${choice.available ? '' : ' disabled'}>${escapeHTML(choice.label)}${choice.available ? '' : ' — Sold out'}</option>`).join('')}</select></div>`
          : `<input type="hidden" data-wishlist-variant value="${availableChoices[0].id}">`;
        quickAdd.innerHTML = `${select}<button class="wishlist-item__add" type="button" data-wishlist-add${needsChoice ? ' disabled' : ''}>Add to cart</button>`;
      } catch (_) {
        if (document.contains(card)) quickAdd.innerHTML = `<a class="wishlist-item__add" href="${item.url}">View product</a>`;
      }
    },
    remove(handle, announce = false) {
      storage.set(this.key, this.items().filter(item => item.handle !== handle));
      this.updateButtons();
      this.render();
      if (announce) wishlistNotice('Removed from wishlist', { subtle: true });
    }
  };

  class ProductPage {
    constructor(section) {
      this.section = section;
      this.variants = JSON.parse(qs('[data-product-json]', section)?.textContent || '[]');
      this.meta = JSON.parse(qs('[data-product-meta]', section)?.textContent || '{}');
      this.form = qs('[data-product-form]', section);
      this.optionGroups = qsa('[data-option-position]', section);
      this.submit = qs('[data-product-submit]', section);
      this.buyNowButton = qs('[data-buy-now]', section);
      this.stickySubmit = qs('[data-sticky-submit]', section);
      this.sizeRequired = section.dataset.sizeRequired === 'true';
      this.deliveryEndpoint = section.dataset.deliveryEndpoint || '';
      this.currentCart = Cart.currentCart;
      this.bind();
      this.updateVariant(false);
      this.rememberProduct();
      if (!this.currentCart) {
        Cart.get().then(cart => { this.currentCart = cart; this.updateInlineQuantity(cart); }).catch(() => {});
      }
    }
    selectedOptions() { return this.optionGroups.map(group => qs('.option-value.is-selected', group)?.dataset.optionValue); }
    currentVariant() {
      if (!this.optionGroups.length) return this.variants[0];
      const options = this.selectedOptions();
      if (options.some(value => !value)) return null;
      return this.variants.find(variant => variant.options.every((value, index) => value === options[index]));
    }
    bind() {
      this.section.addEventListener('click', async event => {
        const quantityChange = event.target.closest('[data-product-quantity-change]');
        if (quantityChange) {
          event.preventDefault();
          await this.updateProductQuantity(Number(quantityChange.dataset.productQuantityChange || 0));
          return;
        }
        const option = event.target.closest('[data-option-value]');
        if (option) {
          const group = option.closest('[data-option-position]');
          qsa('[data-option-value]', group).forEach(button => { button.classList.toggle('is-selected', button === option); button.setAttribute('aria-pressed', String(button === option)); });
          qs('[data-selected-option]', group).textContent = option.dataset.optionValue;
          const error = qs('[data-variant-error]', this.section); if (error) error.hidden = true;
          this.updateVariant();
        }
        if (event.target.closest('[data-sticky-submit]')) this.form?.requestSubmit();
        if (event.target.closest('[data-size-guide-open]')) { const modal = qs('[data-size-guide-modal]', this.section); modal?.showModal(); track('size_guide_open', { item_id: this.section.dataset.productHandle }); }
        if (event.target.closest('[data-size-guide-close]')) qs('[data-size-guide-modal]', this.section)?.close();
        if (event.target.closest('[data-pincode-check]')) this.checkPincode();
        if (event.target.closest('[data-discount-prev]')) this.scrollDiscounts(-1);
        if (event.target.closest('[data-discount-next]')) this.scrollDiscounts(1);
        if (event.target.closest('[data-discount-sheet-open]')) {
          event.preventDefault();
          const sheet = qs('[data-discount-sheet]', this.section);
          if (sheet?.showModal) sheet.showModal();
          else sheet?.setAttribute('open', '');
        }
        if (event.target.closest('[data-discount-sheet-close]') || event.target.matches('[data-discount-sheet]')) {
          const sheet = qs('[data-discount-sheet]', this.section);
          if (typeof sheet?.close === 'function') sheet.close();
          else sheet?.removeAttribute('open');
        }
        if (event.target.closest('[data-buy-now]')) await this.buyNow();
      });
      const pincodeInput = qs('[data-pincode-input]', this.section);
      pincodeInput?.addEventListener('focus', () => {
        const checker = pincodeInput.closest('[data-pincode-checker]');
        loadPincodeEdd(checker?.dataset.eddDataUrl).catch(() => {});
      }, { once: true });
      pincodeInput?.addEventListener('input', () => { pincodeInput.value = pincodeInput.value.replace(/\D/g, '').slice(0, 6); });
      pincodeInput?.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.checkPincode();
      });
      this.form?.addEventListener('submit', async event => {
        event.preventDefault();
        const sizeSelected = !this.sizeRequired || Boolean(qs('[data-option-name="size"] .option-value.is-selected', this.section));
        if (!sizeSelected) { this.showError('Choose a size to continue.'); return; }
        const variant = this.currentVariant();
        if (!variant?.available) { this.showError('That size is currently unavailable. Please choose another.'); return; }
        const quantity = qs('[name="quantity"]', this.form)?.value || 1;
        const submitText = qs('[data-product-submit-text]', this.submit || this.section);
        const originalSubmitText = submitText?.textContent || 'Add to cart';
        if (this.submit) {
          this.submit.disabled = true;
          this.submit.setAttribute('aria-busy', 'true');
        }
        if (this.stickySubmit) {
          this.stickySubmit.disabled = true;
          this.stickySubmit.setAttribute('aria-busy', 'true');
        }
        if (submitText) submitText.textContent = 'Adding…';
        const size = qs('[data-option-name="size"] .option-value.is-selected', this.section)?.dataset.optionValue || '';
        try {
          const addition = await Cart.add(variant.id, quantity, { confirmDuplicate: true, size });
          if (!addition?.cancelled && addition?.cart) {
            this.currentCart = addition.cart;
            this.updateInlineQuantity(addition.cart);
          }
        }
        catch (error) {
          document.dispatchEvent(new CustomEvent('cart:add-failed', { detail: { variantId: Number(variant.id), message: error.message } }));
          this.showError(error.message);
        }
        finally {
          this.submit?.removeAttribute('aria-busy');
          this.stickySubmit?.removeAttribute('aria-busy');
          if (submitText && submitText.textContent === 'Adding…') submitText.textContent = originalSubmitText;
          this.updateVariant(false);
        }
      });
      this.section.addEventListener('size-recommender:add', async event => {
        const button = event.detail?.button;
        const size = String(event.detail?.size || '').trim();
        const restoreButton = () => {
          if (!button) return;
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.textContent = `Add ${size} size to cart`;
        };
        const variant = this.currentVariant();
        if (!variant?.available) {
          restoreButton();
          this.showError('That size is currently unavailable. Please choose another.');
          return;
        }
        const quantity = qs('[name="quantity"]', this.form)?.value || 1;
        try {
          const addition = await Cart.add(variant.id, quantity, { openDrawer: false, confirmDuplicate: true, size });
          if (addition?.cancelled) { restoreButton(); return; }
          if (addition?.cart) {
            this.currentCart = addition.cart;
            this.updateInlineQuantity(addition.cart);
          }
          if (button) {
            button.removeAttribute('aria-busy');
            button.textContent = `Added ${size} to cart ✓`;
          }
          button?.closest('[data-size-guide-modal]')?.close();
          window.themeDrawers?.open('cart-drawer');
          setTimeout(restoreButton, 1800);
        } catch (error) {
          document.dispatchEvent(new CustomEvent('cart:add-failed', { detail: { variantId: Number(variant.id), message: error.message } }));
          restoreButton();
          this.showError(error.message);
        }
      });
      document.addEventListener('cart:updated', event => {
        this.currentCart = event.detail?.cart || this.currentCart;
        this.updateInlineQuantity(this.currentCart);
      });
      const observer = new IntersectionObserver(entries => {
        const sticky = qs('[data-sticky-atc]', this.section);
        if (sticky) sticky.hidden = entries[0].isIntersecting;
      }, { threshold: 0 });
      if (this.submit) observer.observe(this.submit);
      const gallery = qs('[data-product-gallery]', this.section);
      const galleryControls = qs('[data-product-gallery-controls]', this.section);
      if (galleryControls) {
        qs('[data-gallery-prev]', galleryControls)?.addEventListener('click', () => gallery?.scrollBy({ left: -gallery.clientWidth * .9, behavior: 'smooth' }));
        qs('[data-gallery-next]', galleryControls)?.addEventListener('click', () => gallery?.scrollBy({ left: gallery.clientWidth * .9, behavior: 'smooth' }));
      }
      gallery?.addEventListener('scroll', () => { this.updateDots(); this.updateGalleryControls(); }, { passive: true });
      if (gallery && 'ResizeObserver' in window) new ResizeObserver(() => this.updateGalleryControls()).observe(gallery);
      requestAnimationFrame(() => this.updateGalleryControls());
      const discountTrack = qs('[data-discount-track]', this.section);
      discountTrack?.addEventListener('scroll', () => this.updateDiscountProgress(), { passive: true });
      if (discountTrack && 'ResizeObserver' in window) new ResizeObserver(() => this.updateDiscountProgress()).observe(discountTrack);
      requestAnimationFrame(() => this.updateDiscountProgress());
      this.startDiscountAutoplay();
    }
    cartItemForVariant(variantID, cart = this.currentCart) {
      if (!variantID || !cart?.items) return null;
      return cart.items.find(item => Number(item.variant_id || item.id) === Number(variantID)) || null;
    }
    updateInlineQuantity(cart = this.currentCart) {
      const control = qs('[data-product-inline-quantity]', this.section);
      if (!control) return;
      const variant = this.currentVariant();
      const item = this.cartItemForVariant(variant?.id, cart);
      control.hidden = !item;
      control.dataset.lineKey = item?.key || '';
      const value = qs('[data-product-quantity-value]', control);
      if (value) value.textContent = String(item?.quantity || 0);
    }
    async updateProductQuantity(delta) {
      if (!delta) return;
      const variant = this.currentVariant();
      const item = this.cartItemForVariant(variant?.id);
      if (!item) return;
      const control = qs('[data-product-inline-quantity]', this.section);
      const buttons = qsa('button', control || document.createElement('div'));
      buttons.forEach(button => { button.disabled = true; });
      try {
        const cart = await Cart.change(item.key, Math.max(0, Number(item.quantity || 0) + delta));
        this.currentCart = cart || this.currentCart;
        this.updateInlineQuantity(this.currentCart);
      } catch (error) { this.showError(error.message); }
      finally { buttons.forEach(button => { button.disabled = false; }); }
    }
    async buyNow() {
      const sizeSelected = !this.sizeRequired || Boolean(qs('[data-option-name="size"] .option-value.is-selected', this.section));
      if (!sizeSelected) { this.showError('Choose a size to continue.'); return; }
      const variant = this.currentVariant();
      if (!variant?.available) { this.showError('That size is currently unavailable. Please choose another.'); return; }
      const quantity = qs('[name="quantity"]', this.form)?.value || 1;
      const checkoutUrl = this.buyNowButton?.dataset.buyNowUrl || '/checkout';
      const integrationEvent = new CustomEvent('theme:buy-now', { bubbles: true, cancelable: true, detail: { variantId: variant.id, quantity: Number(quantity), checkoutUrl } });
      if (!this.buyNowButton.dispatchEvent(integrationEvent)) return;
      this.buyNowButton.setAttribute('aria-busy', 'true'); this.buyNowButton.disabled = true;
      try { await Cart.add(variant.id, quantity); window.location.assign(checkoutUrl); }
      catch (error) { this.showError(error.message); this.buyNowButton.disabled = false; this.buyNowButton.removeAttribute('aria-busy'); }
    }
    updateVariant(updateUrl = true) {
      const variant = this.currentVariant();
      const incomplete = this.optionGroups.length > 0 && this.selectedOptions().some(value => !value);
      const productAvailable = this.variants.some(item => item.available);
      const idInput = qs('[data-variant-id]', this.section);
      if (idInput) idInput.value = variant?.id || '';
      const available = Boolean(variant?.available);
      if (this.submit) this.submit.disabled = incomplete ? !productAvailable : !available;
      if (this.buyNowButton) this.buyNowButton.disabled = incomplete ? !productAvailable : !available;
      if (this.stickySubmit) this.stickySubmit.disabled = incomplete ? !productAvailable : !available;
      qsa('[data-product-submit-text]', this.section).forEach(text => { text.textContent = incomplete ? 'Add to cart' : available ? 'Add to cart' : variant ? 'Sold out' : 'Unavailable'; });
      const priceContainer = qs('.product-info > [data-price]', this.section);
      if (priceContainer && variant) {
        const onSale = Number(variant.compare_at_price || 0) > Number(variant.price);
        const saving = onSale ? Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100) : 0;
        priceContainer.classList.toggle('price--sale', onSale);
        priceContainer.innerHTML = `<span class="price__current">${money(variant.price)}</span>${onSale ? `<s class="price__compare">${money(variant.compare_at_price)}</s><span class="price__save">${saving}% off</span>` : ''}`;
      }
      if (variant) {
        qsa('[data-value-summary]', this.section).forEach(summary => {
          const heading = qs('[data-value-heading]', summary); if (!heading) return;
          const useMRP = summary.dataset.priceSource === 'mrp';
          const amount = useMRP && Number(variant.compare_at_price || 0) > 0 ? Number(variant.compare_at_price) : Number(variant.price);
          heading.textContent = (heading.dataset.headingTemplate || 'What you get for [price]').replace(/\[price\]/gi, money(amount));
        });
      }
      const stickyPrice = qs('[data-sticky-price]', this.section);
      if (stickyPrice && variant) stickyPrice.textContent = money(variant.price);
      const stickyVariant = qs('[data-sticky-variant]', this.section);
      if (stickyVariant) stickyVariant.textContent = variant ? variant.title : this.sizeRequired ? 'Choose a size' : '';
      const stock = qs('[data-stock-message]', this.section);
      if (stock) {
        const quantity = Number(variant?.inventory_quantity || 0);
        stock.textContent = available && variant.inventory_management && quantity > 0 && quantity <= Number(this.meta.low_stock_threshold) ? `Only ${quantity} left in this size` : '';
      }
      this.updateAvailability();
      this.updateInlineQuantity();
      if (variant && updateUrl) history.replaceState({}, '', `${location.pathname}?variant=${variant.id}`);
      if (variant) track('select_item_variant', { item_id: variant.id, item_variant: variant.title, price: variant.price / 100 });
    }
    updateAvailability() {
      const selected = this.selectedOptions();
      this.optionGroups.forEach((group, optionIndex) => {
        qsa('[data-option-value]', group).forEach(button => {
          const candidate = [...selected]; candidate[optionIndex] = button.dataset.optionValue;
          const possible = this.variants.some(variant => variant.available && variant.options.every((value, index) => !candidate[index] || value === candidate[index]));
          button.classList.toggle('is-unavailable', !possible);
        });
      });
    }
    showError(message) { const error = qs('[data-variant-error]', this.section); if (error) { error.textContent = message; error.hidden = false; } toast(message); }
    async checkPincode() {
      const input = qs('[data-pincode-input]', this.section); const result = qs('[data-pincode-result]', this.section); const button = qs('[data-pincode-check]', this.section);
      const checker = input?.closest('[data-pincode-checker]'); const buttonText = qs('[data-pincode-button-text]', button);
      const disclaimer = qs('[data-pincode-disclaimer]', checker);
      if (!input || !result || !checker) return;
      const setResult = (message, status) => {
        result.textContent = message;
        result.dataset.status = status;
        result.hidden = !message;
        if (disclaimer) disclaimer.hidden = status !== 'success';
      };
      const setSuccessResult = range => {
        result.replaceChildren();
        result.dataset.status = 'success';
        result.hidden = false;
        const copy = document.createElement('span');
        copy.className = 'pincode-checker__result-copy';
        const delivery = document.createElement('span');
        delivery.className = 'pincode-checker__result-edd';
        const deliveryLabel = document.createElement('span');
        deliveryLabel.className = 'pincode-checker__result-label';
        deliveryLabel.textContent = `${checker.dataset.successLabel || 'Estimated delivery'}: `;
        const deliveryRange = document.createElement('strong');
        deliveryRange.className = 'pincode-checker__result-range';
        deliveryRange.textContent = range;
        delivery.append(deliveryLabel, deliveryRange);
        copy.appendChild(delivery);
        if (checker.dataset.showCod === 'true') {
          const cod = document.createElement('span');
          cod.className = 'pincode-checker__result-cod';
          const check = document.createElement('span');
          check.className = 'pincode-checker__result-cod-check';
          check.setAttribute('aria-hidden', 'true');
          check.textContent = '✓';
          const text = document.createElement('span');
          text.textContent = checker.dataset.codSuccessText || 'COD available';
          cod.append(check, text);
          copy.appendChild(cod);
        }
        result.appendChild(copy);
        if (disclaimer) disclaimer.hidden = false;
      };
      const pincode = input.value.trim();
      if (!/^[1-9][0-9]{5}$/.test(pincode)) { setResult('Enter a valid 6-digit Indian PIN code.', 'error'); input.focus({ preventScroll: true }); return; }
      const originalButtonLabel = buttonText?.textContent || 'Check';
      setResult(checker.dataset.loadingText || 'Checking delivery…', 'loading');
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      if (buttonText) buttonText.textContent = 'Checking';
      try {
        const lookup = await loadPincodeEdd(checker.dataset.eddDataUrl);
        const eddDays = Number(lookup?.[pincode]);
        if (!Number.isFinite(eddDays)) {
          setResult(checker.dataset.unavailableText || 'Delivery is not available to this PIN code yet.', 'unavailable');
        } else {
          const range = formatDeliveryRange(eddDays, checker.dataset.eddBuffer || 2);
          setSuccessResult(range);
        }
        track('check_delivery_pincode', { pincode_prefix: pincode.slice(0, 3), source: 'historical_edd', available: Number.isFinite(eddDays), edd_days: Number.isFinite(eddDays) ? eddDays : undefined });
      } catch (_) { setResult(checker.dataset.errorText || 'We could not check delivery right now. Please try again shortly.', 'error'); }
      finally {
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
        if (buttonText) buttonText.textContent = originalButtonLabel;
      }
    }
    scrollDiscounts(direction) { const track = qs('[data-discount-track]', this.section); track?.scrollBy({ left: direction * track.clientWidth * .88, behavior: 'smooth' }); }
    startDiscountAutoplay() {
      const slider = qs('[data-discount-slider]', this.section);
      const track = qs('[data-discount-track]', slider || this.section);
      const slides = qsa('.discount-slide', track || document.createElement('div'));
      if (!slider || !track || slider.dataset.discountAutoplay !== 'true' || slides.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const interval = Math.max(2000, Number(slider.dataset.discountInterval || 4000));
      window.setInterval(() => {
        if (document.hidden || slider.matches(':hover') || slider.contains(document.activeElement)) return;
        const center = track.scrollLeft + track.clientWidth / 2;
        let active = 0;
        let distance = Infinity;
        slides.forEach((slide, index) => {
          const delta = Math.abs(center - (slide.offsetLeft + slide.offsetWidth / 2));
          if (delta < distance) { active = index; distance = delta; }
        });
        const next = slides[(active + 1) % slides.length];
        track.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
      }, interval);
    }
    updateDiscountProgress() {
      const track = qs('[data-discount-track]', this.section); if (!track) return;
      const slides = qsa('.discount-slide', track); const indicators = qsa('[data-discount-progress] i', this.section); if (!slides.length || !indicators.length) return;
      const center = track.scrollLeft + track.clientWidth / 2;
      let active = 0, distance = Infinity;
      slides.forEach((slide, index) => { const delta = Math.abs(center - (slide.offsetLeft + slide.offsetWidth / 2)); if (delta < distance) { active = index; distance = delta; } });
      indicators.forEach((indicator, index) => indicator.classList.toggle('is-active', index === active));
    }
    updateDots() {
      const gallery = qs('[data-product-gallery]', this.section); if (!gallery) return;
      const items = qsa('[data-media-id]', gallery); if (!items.length) return;
      const center = gallery.scrollLeft + gallery.clientWidth / 2;
      let active = 0, distance = Infinity;
      items.forEach((item, index) => { const itemCenter = item.offsetLeft + item.offsetWidth / 2; const delta = Math.abs(center - itemCenter); if (delta < distance) { active = index; distance = delta; } });
      qsa('.gallery-dots span', this.section).forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    }
    updateGalleryControls() {
      const gallery = qs('[data-product-gallery]', this.section);
      const controls = qs('[data-product-gallery-controls]', this.section);
      if (!gallery || !controls) return;
      const maximum = Math.max(0, gallery.scrollWidth - gallery.clientWidth);
      const ratio = maximum ? gallery.scrollLeft / maximum : 0;
      const previous = qs('[data-gallery-prev]', controls);
      const next = qs('[data-gallery-next]', controls);
      const progress = qs('[data-gallery-progress]', controls);
      if (previous) previous.disabled = gallery.scrollLeft < 4;
      if (next) next.disabled = gallery.scrollLeft > maximum - 4 || maximum === 0;
      if (progress) progress.style.width = `${Math.max(18, 18 + ratio * 82)}%`;
      if (controls.classList.contains('scroll-controls--inside')) {
        const shell = gallery.closest('.product-gallery-shell');
        const media = qs('[data-media-id]', gallery);
        if (shell && media) {
          const shellRect = shell.getBoundingClientRect();
          const mediaRect = media.getBoundingClientRect();
          shell.style.setProperty('--slider-thumbnail-center', `${mediaRect.top - shellRect.top + mediaRect.height / 2}px`);
        }
      }
    }
    rememberProduct() {
      const handle = this.section.dataset.productHandle; if (!handle) return;
      const recent = storage.get('bluberyl:recent', []).filter(item => item !== handle); recent.unshift(handle); storage.set('bluberyl:recent', recent.slice(0, 12));
    }
  }

  async function renderRecentlyViewed() {
    qsa('[data-recently-viewed]').forEach(async section => {
      const current = section.dataset.currentHandle;
      const handles = storage.get('bluberyl:recent', []).filter(handle => handle !== current).slice(0, Number(section.dataset.limit || 6));
      if (!handles.length) return;
      const products = (await Promise.all(handles.map(handle => fetch(`${root}products/${handle}.js`).then(response => response.ok ? response.json() : null).catch(() => null)))).filter(Boolean);
      if (!products.length) return;
      const grid = qs('[data-recently-viewed-grid]', section);
      const imageRatio = section.dataset.imageRatio || 'adapt';
      const compactClass = section.dataset.compactCards === 'true' ? ' product-card--compact' : '';
      grid.innerHTML = products.map(product => `<article class="product-card${compactClass}"><div class="product-card__media media media--${imageRatio}"><a class="product-card__image-link" href="${product.url}"><img class="product-card__image" src="${product.featured_image}&width=700" alt="${escapeHTML(product.title)}" width="560" height="700" loading="lazy"></a></div><div class="product-card__content"><h3 class="product-card__title"><a href="${product.url}">${escapeHTML(product.title)}</a></h3><div class="price"><span class="price__current">${money(product.price)}</span></div></div></article>`).join('');
      section.hidden = false;
      grid.dispatchEvent(new Event('scroll'));
    });
  }

  function initPredictiveSearch() {
    qsa('[data-predictive-search-input]').forEach(input => {
      const output = qs('[data-predictive-search]', input.closest('.search-drawer'));
      let timer;
      input.addEventListener('input', () => {
        clearTimeout(timer); const query = input.value.trim();
        if (query.length < 2) { output.innerHTML = ''; return; }
        timer = setTimeout(async () => {
          try {
            const response = await fetch(`${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=6`);
            const data = await response.json(); const results = data.resources?.results || {};
            const products = results.products || []; const collections = results.collections || [];
            output.innerHTML = [...products.map(product => `<a class="predictive-search__item" href="${product.url}">${product.image ? `<img src="${product.image}&width=120" alt="" width="58" height="72">` : ''}<span>${escapeHTML(product.title)}<br><small>${decimalMoney(product.price)}</small></span></a>`), ...collections.map(collection => `<a class="predictive-search__item" href="${collection.url}"><span>Collection<br><strong>${escapeHTML(collection.title)}</strong></span></a>`)].join('');
            track('search', { search_term: query });
          } catch { output.innerHTML = '<p>Search is temporarily unavailable.</p>'; }
        }, 220);
      });
    });
  }

  function initCollectionCardMedia(scope = document) {
    qsa('[data-card-media-track]', scope).forEach(track => {
      if (track.dataset.cardMediaBound === 'true') return;
      track.dataset.cardMediaBound = 'true';
      const dots = qsa('i', qs('[data-card-media-dots]', track.closest('[data-card-media-slider]')) || document.createElement('div'));
      let frame;
      let pointerStartX = 0;
      let pointerStartScroll = 0;
      let dragged = false;

      const updateDots = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = track.clientWidth || 1;
          const active = Math.max(0, Math.min(dots.length - 1, Math.round(Math.abs(track.scrollLeft) / width)));
          dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
        });
      };

      track.addEventListener('pointerdown', event => {
        pointerStartX = event.clientX;
        pointerStartScroll = track.scrollLeft;
        dragged = false;
        track.dataset.userInteracted = 'true';
        if (event.pointerType === 'mouse') track.classList.add('is-dragging');
      });
      track.addEventListener('pointermove', event => {
        const movement = event.clientX - pointerStartX;
        if (Math.abs(movement) > 7) dragged = true;
        if (event.pointerType === 'mouse' && event.buttons === 1 && dragged) {
          event.preventDefault();
          track.scrollLeft = pointerStartScroll - movement;
        }
      });
      const endDrag = () => {
        track.classList.remove('is-dragging');
        setTimeout(() => { dragged = false; }, 0);
      };
      track.addEventListener('pointerup', endDrag);
      track.addEventListener('pointercancel', endDrag);
      track.addEventListener('click', event => {
        if (!dragged && Math.abs(track.scrollLeft - pointerStartScroll) < 7) return;
        if (event.target.closest('[data-card-media-link]')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, true);
      track.addEventListener('scroll', updateDots, { passive: true });
      updateDots();
    });
  }

  function scheduleCollectionCardNudge(section) {
    if (!section || section.dataset.cardSwipeEnabled !== 'true' || section.dataset.cardSwipeNudge !== 'true') return;
    if (!matchMedia('(max-width: 989px)').matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const track = qs('[data-card-media-track]', section);
    if (!track || qsa('[data-card-media-link]', track).length < 2 || track.dataset.nudgeScheduled === 'true') return;
    track.dataset.nudgeScheduled = 'true';
    const delay = Math.max(1000, Number(section.dataset.cardSwipeNudgeDelay || 2000));
    const cancel = () => {
      track.dataset.userInteracted = 'true';
      clearTimeout(timer);
    };
    const timer = setTimeout(() => {
      if (track.dataset.userInteracted === 'true') return;
      const distance = Math.min(82, Math.max(34, track.clientWidth * .18));
      track.classList.add('is-nudging');
      track.scrollTo({ left: distance, behavior: 'smooth' });
      setTimeout(() => track.scrollTo({ left: 0, behavior: 'smooth' }), 430);
      setTimeout(() => track.classList.remove('is-nudging'), 1000);
    }, delay);
    track.addEventListener('pointerdown', cancel, { once: true });
    track.addEventListener('touchstart', cancel, { once: true, passive: true });
    track.addEventListener('wheel', cancel, { once: true, passive: true });
  }

  function initCollectionCategorySheet(scope = document) {
    const sheet = qs('[data-category-sheet]', scope) || qs('[data-category-sheet]');
    if (!sheet || sheet.dataset.categorySheetBound === 'true') return;
    sheet.dataset.categorySheetBound = 'true';
    const handle = qs('[data-category-sheet-handle]', sheet);
    let returnFocus;
    let startY = 0;
    let dragDistance = 0;

    const restoreFocus = () => {
      document.body.classList.remove('category-sheet-open');
      sheet.classList.remove('is-dragging');
      sheet.style.removeProperty('transform');
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      returnFocus = null;
    };
    const closeSheet = () => {
      if (sheet.open && typeof sheet.close === 'function') sheet.close();
      else {
        sheet.removeAttribute('open');
        restoreFocus();
      }
    };
    const openSheet = trigger => {
      returnFocus = trigger;
      if (typeof sheet.showModal === 'function') sheet.showModal();
      else sheet.setAttribute('open', '');
      document.body.classList.add('category-sheet-open');
      qs('[data-category-sheet-close]', sheet)?.focus({ preventScroll: true });
    };

    qsa('[data-category-sheet-open]').forEach(trigger => {
      if (trigger.dataset.categorySheetTriggerBound === 'true') return;
      trigger.dataset.categorySheetTriggerBound = 'true';
      trigger.addEventListener('click', () => openSheet(trigger));
    });
    qsa('[data-category-sheet-close]', sheet).forEach(button => button.addEventListener('click', closeSheet));
    sheet.addEventListener('click', event => { if (event.target === sheet) closeSheet(); });
    sheet.addEventListener('cancel', event => { event.preventDefault(); closeSheet(); });
    sheet.addEventListener('close', restoreFocus);

    handle?.addEventListener('pointerdown', event => {
      startY = event.clientY;
      dragDistance = 0;
      sheet.classList.add('is-dragging');
      handle.setPointerCapture?.(event.pointerId);
    });
    handle?.addEventListener('pointermove', event => {
      if (!sheet.classList.contains('is-dragging')) return;
      dragDistance = Math.max(0, event.clientY - startY);
      sheet.style.transform = `translateY(${dragDistance}px)`;
    });
    const endSheetDrag = event => {
      if (!sheet.classList.contains('is-dragging')) return;
      handle?.releasePointerCapture?.(event.pointerId);
      sheet.classList.remove('is-dragging');
      if (dragDistance > Math.min(120, sheet.clientHeight * .2)) closeSheet();
      else sheet.style.removeProperty('transform');
    };
    handle?.addEventListener('pointerup', endSheetDrag);
    handle?.addEventListener('pointercancel', endSheetDrag);
  }

  function initCollection() {
    const facets = qs('[data-facets-drawer]'); const overlay = qs('[data-drawer-overlay]');
    qs('[data-facets-open]')?.addEventListener('click', () => {
      if (matchMedia('(max-width: 989px)').matches) {
        facets?.classList.add('is-open');
        if (overlay) overlay.hidden = false;
        document.body.classList.add('is-locked');
      } else {
        facets?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        qs('summary', facets)?.focus({ preventScroll: true });
      }
      track('view_item_list_filters');
    });
    qs('[data-facets-close]')?.addEventListener('click', closeFacets);
    function closeFacets() { facets?.classList.remove('is-open'); if (overlay) overlay.hidden = true; document.body.classList.remove('is-locked'); }
    qs('[data-sort-select]')?.addEventListener('change', event => { const hidden = qs('[name="sort_by"]', qs('[data-facets-form]')); if (hidden) hidden.value = event.target.value; qs('[data-facets-form]')?.submit(); });
    const collectionSection = qs('[data-collection-section]');
    const collectionGrid = qs('[data-product-grid]', collectionSection || document);
    const viewButtons = qsa('[data-collection-view]', collectionSection || document);
    initCollectionCardMedia(collectionSection || document);
    initCollectionCategorySheet(collectionSection || document);
    scheduleCollectionCardNudge(collectionSection);
    const applyCollectionView = view => {
      if (!collectionGrid) return;
      const selectedView = view === 'list' ? 'list' : 'grid';
      collectionGrid.classList.toggle('product-grid--collection-list', selectedView === 'list');
      viewButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.collectionView === selectedView)));
    };
    if (collectionGrid && viewButtons.length) {
      const defaultView = collectionGrid.dataset.defaultCollectionView || 'grid';
      const savedView = storage.get('bluberyl:collection-view', defaultView);
      applyCollectionView(savedView);
      viewButtons.forEach(button => button.addEventListener('click', () => {
        const view = button.dataset.collectionView;
        storage.set('bluberyl:collection-view', view);
        applyCollectionView(view);
      }));
    }
    const loadMore = qs('[data-load-more]', collectionSection || document);
    let loadingMore = false;
    let infiniteObserver;
    const loadNextPage = async () => {
      if (!loadMore || loadingMore) return;
      loadingMore = true; loadMore.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(loadMore.href);
        if (!response.ok) throw new Error('Pagination request failed');
        const html = await response.text(); const doc = new DOMParser().parseFromString(html, 'text/html');
        const incoming = qsa('[data-product-grid] > .product-card', doc); const grid = qs('[data-product-grid]', collectionSection || document); incoming.forEach(card => grid?.append(card)); initCollectionCardMedia(grid || document);
        const next = qs('[data-load-more]', doc);
        if (next) { loadMore.href = next.href; loadMore.innerHTML = next.innerHTML; }
        else { infiniteObserver?.disconnect(); loadMore.remove(); }
      } catch {
        infiniteObserver?.disconnect();
        toast('Could not load more products.');
      } finally {
        loadingMore = false; loadMore?.removeAttribute('aria-busy');
      }
    };
    loadMore?.addEventListener('click', event => { event.preventDefault(); loadNextPage(); });
    if (loadMore && collectionSection?.dataset.loadMode === 'infinite' && 'IntersectionObserver' in window) {
      infiniteObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) loadNextPage();
      }, { rootMargin: '600px 0px' });
      infiniteObserver.observe(loadMore);
    }
  }

  function initRecommendations() {
    qsa('product-recommendations[data-url]').forEach(async element => {
      if (element.dataset.loaded) return; element.dataset.loaded = 'true';
      try { const response = await fetch(element.dataset.url); const html = await response.text(); const doc = new DOMParser().parseFromString(html, 'text/html'); const replacement = qs('product-recommendations', doc); if (replacement?.innerHTML.trim()) { element.innerHTML = replacement.innerHTML; initScrollSliders(element); } } catch (_) {}
    });
  }

  function initHeroSliders() {
    qsa('[data-hero-slider]').forEach(slider => {
      const slides = qsa('[data-hero-slide]', slider); const dots = qsa('[data-hero-dot]', slider); if (slides.length < 2) return;
      let current = 0; let timer;
      const show = index => {
        current = (index + slides.length) % slides.length;
        slides.forEach((slide, position) => { const active = position === current; slide.classList.toggle('is-active', active); slide.setAttribute('aria-hidden', String(!active)); });
        dots.forEach((dot, position) => dot.setAttribute('aria-selected', String(position === current)));
        if (slider.dataset.heightMode === 'adapt') {
          slider.style.setProperty('--hero-desktop-ratio', slides[current].dataset.desktopRatio || '1.7778');
          slider.style.setProperty('--hero-mobile-ratio', slides[current].dataset.mobileRatio || slides[current].dataset.desktopRatio || '.8');
        }
      };
      const stop = () => clearInterval(timer);
      const start = () => {
        stop();
        if (slider.dataset.autoplay === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) timer = setInterval(() => show(current + 1), Number(slider.dataset.interval || 5000));
      };
      qs('[data-hero-prev]', slider)?.addEventListener('click', () => { show(current - 1); start(); });
      qs('[data-hero-next]', slider)?.addEventListener('click', () => { show(current + 1); start(); });
      dots.forEach(dot => dot.addEventListener('click', () => { show(Number(dot.dataset.heroDot)); start(); }));
      slider.addEventListener('mouseenter', stop); slider.addEventListener('mouseleave', start); slider.addEventListener('focusin', stop); slider.addEventListener('focusout', start);
      start();
    });
  }

  function initScrollSliders(scope = document) {
    const sliders = scope.matches?.('[data-scroll-slider]') ? [scope, ...qsa('[data-scroll-slider]', scope)] : qsa('[data-scroll-slider]', scope);
    sliders.forEach(slider => {
      if (slider.dataset.scrollBound === 'true') return;
      const track = qs('[data-scroll-track]', slider); const controlsList = qsa('[data-scroll-controls]', slider); if (!track || !controlsList.length) return;
      slider.dataset.scrollBound = 'true';
      const update = () => {
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth); const ratio = maximum ? track.scrollLeft / maximum : 0;
        controlsList.forEach(controls => {
          const previous = qs('[data-list-prev]', controls); const next = qs('[data-list-next]', controls); const progress = qs('[data-scroll-progress]', controls);
          if (progress) {
            const startingWidth = controls.classList.contains('scroll-controls--edge_to_edge_line') ? 8 : 18;
            progress.style.width = `${startingWidth + ratio * (100 - startingWidth)}%`;
          }
          if (previous) previous.disabled = track.scrollLeft < 4;
          if (next) next.disabled = track.scrollLeft > maximum - 4 || maximum === 0;
          if (controls.classList.contains('scroll-controls--inside')) {
            const frame = track.closest('[data-slider-frame]');
            const thumbnail = qs('.product-card__media', track);
            if (frame && thumbnail) {
              const frameRect = frame.getBoundingClientRect();
              const thumbnailRect = thumbnail.getBoundingClientRect();
              frame.style.setProperty('--slider-thumbnail-center', `${thumbnailRect.top - frameRect.top + thumbnailRect.height / 2}px`);
            }
          }
        });
      };
      controlsList.forEach(controls => {
        qs('[data-list-prev]', controls)?.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * .82, behavior: 'smooth' }));
        qs('[data-list-next]', controls)?.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * .82, behavior: 'smooth' }));
      });
      track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();
    });
  }

  function positionActiveCollectionPills(scope = document) {
    const switchers = scope.matches?.('.text-collection-switcher') ? [scope, ...qsa('.text-collection-switcher', scope)] : qsa('.text-collection-switcher', scope);
    switchers.forEach(switcher => {
      if (switcher.dataset.activePillPositioned === 'true' || switcher.classList.contains('text-collection-switcher--marquee')) return;
      const track = qs('.text-collection-switcher__track', switcher);
      const pills = track ? qsa('.text-collection-switcher__pill', track) : [];
      const active = pills.find(pill => pill.classList.contains('is-active'));
      if (!track || !active || pills.length < 2) return;
      switcher.dataset.activePillPositioned = 'true';

      requestAnimationFrame(() => requestAnimationFrame(() => {
        const activeIndex = pills.indexOf(active);
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        let target = 0;
        if (activeIndex === pills.length - 1) target = maximum;
        else if (activeIndex > 0) {
          const trackRect = track.getBoundingClientRect();
          const activeRect = active.getBoundingClientRect();
          const activeCenter = activeRect.left - trackRect.left + track.scrollLeft + activeRect.width / 2;
          target = activeCenter - track.clientWidth / 2;
        }
        track.scrollTo({ left: Math.max(0, Math.min(maximum, target)), behavior: 'auto' });
      }));
    });
  }

  function initFeaturedCollectionNudges(scope = document) {
    const sections = qsa('[data-featured-collection][data-scroll-nudge="true"]', scope);
    if (!sections.length || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let acknowledged = false;
    try { acknowledged = sessionStorage.getItem('bluberyl:featured-scroll-ack') === '1'; } catch (_) {}
    const cancellations = new Set();
    const acknowledge = () => {
      if (acknowledged) return;
      acknowledged = true;
      try { sessionStorage.setItem('bluberyl:featured-scroll-ack', '1'); } catch (_) {}
      cancellations.forEach(cancel => cancel());
    };

    sections.forEach(section => {
      if (section.dataset.nudgeBound === 'true') return;
      section.dataset.nudgeBound = 'true';
      const track = qs('[data-scroll-track]', section);
      if (!track) return;
      let timer;
      let pointerStart;
      const cancel = () => { clearTimeout(timer); timer = undefined; };
      cancellations.add(cancel);
      const schedule = () => {
        cancel();
        if (acknowledged || section.dataset.nudged === 'true' || track.scrollLeft > 3 || track.scrollWidth <= track.clientWidth + 4) return;
        timer = setTimeout(() => {
          if (acknowledged || section.dataset.nudged === 'true' || track.scrollLeft > 3) return;
          section.dataset.nudged = 'true';
          const distance = Math.min(42, track.clientWidth * .1);
          section.classList.add('featured-collection--nudging');
          track.scrollTo({ left: distance, behavior: 'smooth' });
          setTimeout(() => track.scrollTo({ left: 0, behavior: 'smooth' }), 420);
          setTimeout(() => section.classList.remove('featured-collection--nudging'), 900);
        }, Number(section.dataset.scrollNudgeDelay || 3000));
      };
      track.addEventListener('pointerdown', event => { pointerStart = event.clientX; }, { passive: true });
      track.addEventListener('pointermove', event => {
        if (pointerStart == null || Math.abs(event.clientX - pointerStart) < 10) return;
        pointerStart = undefined;
        acknowledge();
      }, { passive: true });
      track.addEventListener('pointerup', () => { pointerStart = undefined; }, { passive: true });
      track.addEventListener('pointercancel', () => { pointerStart = undefined; }, { passive: true });
      track.addEventListener('wheel', event => { if (Math.abs(event.deltaX) > 4) acknowledge(); }, { passive: true });
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && entries[0].intersectionRatio >= .55) schedule();
        else cancel();
      }, { threshold: [.55] });
      observer.observe(section);
    });
  }

  function initShoppableReels() {
    qsa('[data-shoppable-reels]').forEach(section => {
      if (section.dataset.reelsBound === 'true') return;
      section.dataset.reelsBound = 'true';
      const viewer = qs('[data-reels-viewer]', section);
      const feed = qs('[data-reels-feed]', section);
      const slides = qsa('[data-reels-slide]', section);
      const viewerVideos = qsa('.reels-viewer__video', section);
      const gesture = qs('[data-reels-gesture]', section);
      let gestureTimer;
      let gestureObserver;
      const dismissGesture = () => {
        if (!gesture) return;
        clearTimeout(gestureTimer);
        gestureObserver?.disconnect();
        gesture?.classList.remove('is-visible');
        gesture?.classList.add('is-dismissed');
        try { sessionStorage.setItem('bluberyl:reels-gesture-seen', '1'); } catch (_) {}
      };
      if (gesture) {
        let seen = false;
        try { seen = sessionStorage.getItem('bluberyl:reels-gesture-seen') === '1'; } catch (_) {}
        if (seen) gesture.classList.add('is-dismissed');
        else if ('IntersectionObserver' in window) {
          gestureObserver = new IntersectionObserver(entries => {
            if (!entries[0].isIntersecting || entries[0].intersectionRatio < .48) { clearTimeout(gestureTimer); return; }
            gestureObserver.disconnect();
            gestureTimer = setTimeout(() => {
              gesture.classList.add('is-visible');
              try { sessionStorage.setItem('bluberyl:reels-gesture-seen', '1'); } catch (_) {}
              gestureTimer = setTimeout(dismissGesture, 4700);
            }, Number(section.dataset.reelsGestureDelay || 2000));
          }, { threshold: [.48] });
          gestureObserver.observe(section);
        }
        qs('[data-reels-track]', section)?.addEventListener('pointerdown', dismissGesture, { passive: true, once: true });
        qs('[data-reels-track]', section)?.addEventListener('scroll', dismissGesture, { passive: true, once: true });
      }
      const pauseViewer = () => viewerVideos.forEach(video => video.pause());
      const playSlide = slide => {
        viewerVideos.forEach(video => { if (!slide?.contains(video)) video.pause(); });
        const video = qs('video', slide);
        if (video) { video.muted = true; video.playsInline = true; video.play().catch(() => {}); }
      };
      const closeViewer = () => {
        pauseViewer();
        if (viewer?.open) viewer.close();
      };
      const openViewer = index => {
        if (!viewer || !feed || !slides[index]) return;
        dismissGesture();
        if (typeof viewer.showModal === 'function') viewer.showModal(); else viewer.setAttribute('open', '');
        requestAnimationFrame(() => {
          feed.scrollTo({ top: slides[index].offsetTop, behavior: 'auto' });
          playSlide(slides[index]);
        });
      };
      section.addEventListener('click', event => {
        const opener = event.target.closest('[data-reel-open]');
        if (opener) openViewer(Number(opener.dataset.reelOpen || 0));
        if (event.target.closest('[data-reels-close]') || event.target === viewer) closeViewer();
      });
      viewer?.addEventListener('close', pauseViewer);
      if (feed && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visible && viewer?.open) playSlide(visible.target);
        }, { root: feed, threshold: [.58, .78] });
        slides.forEach(slide => observer.observe(slide));
      }
      const initializePlayback = () => {
        const clickOnly = section.dataset.autoplay !== 'true' || matchMedia('(prefers-reduced-motion: reduce)').matches;
        section.classList.toggle('shoppable-reels--click-only', clickOnly);
        section.dataset.reelPlayback = clickOnly ? 'click' : 'autoplay-all';
        if (clickOnly) return;
        qsa('.reel-card', section).forEach(card => {
          const video = qs('video', card); if (!video) return;
          video.muted = true; video.playsInline = true;
          video.play().then(() => card.classList.add('is-playing')).catch(() => card.classList.remove('is-playing'));
        });
      };
      if (document.readyState === 'complete') initializePlayback();
      else window.addEventListener('load', () => setTimeout(initializePlayback, 0), { once: true });
    });
  }

  function initCategoryProducts(scope = document) {
    qsa('[data-category-products]', scope).forEach(section => {
      if (section.dataset.categoryProductsBound === 'true') return;
      section.dataset.categoryProductsBound = 'true';

      const tabs = qsa('[data-category-tab]', section);
      const panels = qsa('[data-category-panel]', section);

      const updateProgress = panel => {
        const track = qs('[data-category-track]', panel);
        const indicator = qs('[data-category-progress]', panel);
        if (!track || !indicator || panel.hidden) return;
        requestAnimationFrame(() => {
          const trackWidth = track.clientWidth;
          const contentWidth = track.scrollWidth;
          if (!trackWidth || !contentWidth) return;
          const visible = Math.min(100, Math.max(12, trackWidth / contentWidth * 100));
          const maximum = Math.max(0, contentWidth - trackWidth);
          const current = Math.min(maximum, Math.abs(track.scrollLeft));
          const left = maximum ? current / maximum * (100 - visible) : 0;
          indicator.style.width = `${visible}%`;
          indicator.style.left = `${left}%`;
        });
      };

      const activate = (tab, resetScroll = true) => {
        const panelID = tab?.getAttribute('aria-controls');
        tabs.forEach(item => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.id !== panelID; });
        qsa('[data-quick-add-panel]', section).forEach(panel => { panel.hidden = true; });
        const activePanel = panels.find(panel => panel.id === panelID);
        const track = qs('[data-category-track]', activePanel);
        if (resetScroll && track) track.scrollLeft = 0;
        if (activePanel) updateProgress(activePanel);
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else nextIndex = (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + tabs.length) % tabs.length;
          tabs[nextIndex]?.focus({ preventScroll: true });
          activate(tabs[nextIndex]);
        });
      });

      panels.forEach(panel => {
        const track = qs('[data-category-track]', panel);
        if (!track) return;
        track.addEventListener('scroll', () => updateProgress(panel), { passive: true });
        if ('ResizeObserver' in window) new ResizeObserver(() => updateProgress(panel)).observe(track);
      });

      if (tabs.length) activate(tabs.find(tab => tab.classList.contains('is-active')) || tabs[0], false);
    });
  }

  function initProductBalance(scope = document) {
    const desktop = matchMedia('(min-width: 990px)');
    qsa('[data-product-section]', scope).forEach(section => {
      if (section.dataset.balanceBound === 'true') return;
      const gallery = qs('.product-gallery-shell', section);
      const leftColumn = qs('[data-product-balanced-column]', section);
      const info = qs('.product-info', section);
      const items = qsa('[data-product-balance-item]', info);
      if (!gallery || !leftColumn || !info || !items.length) return;

      section.dataset.balanceBound = 'true';
      const anchors = items.map(item => {
        const anchor = document.createElement('span');
        anchor.hidden = true;
        anchor.dataset.productBalanceAnchor = '';
        item.before(anchor);
        return anchor;
      });
      let frame;

      const restore = () => {
        items.forEach((item, index) => anchors[index]?.after(item));
        section.classList.remove('product-main--balanced');
      };

      const arrange = () => {
        cancelAnimationFrame(frame);
        restore();
        if (!desktop.matches) return;
        frame = requestAnimationFrame(() => {
          const minimumImbalance = 220;
          if (info.offsetHeight - gallery.offsetHeight < minimumImbalance) return;
          let moveToLeft = true;

          items.forEach(item => {
            if (moveToLeft && info.offsetHeight - gallery.offsetHeight >= minimumImbalance) leftColumn.append(item);
            moveToLeft = !moveToLeft;
          });
          section.classList.toggle('product-main--balanced', leftColumn.childElementCount > 0);
        });
      };

      let resizeTimer;
      const queueArrange = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(arrange, 120);
      };
      desktop.addEventListener?.('change', arrange);
      window.addEventListener('resize', queueArrange, { passive: true });
      qsa('.product-gallery img', gallery).forEach(image => {
        if (!image.complete) image.addEventListener('load', queueArrange, { once: true });
      });
      arrange();
    });
  }

  function initBenefitSliders(scope = document) {
    qsa('[data-benefit-slider]', scope).forEach(track => {
      if (track.dataset.dotsInitialized === 'true') return;
      const dots = track.nextElementSibling;
      const items = qsa('.product-benefit', track);
      if (!dots?.matches('[data-benefit-dots]') || !items.length) return;
      track.dataset.dotsInitialized = 'true';

      const update = () => {
        const indicators = qsa('i', dots);
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        const active = maximum && indicators.length > 1 ? Math.round((track.scrollLeft / maximum) * (indicators.length - 1)) : 0;
        indicators.forEach((indicator, index) => indicator.classList.toggle('is-active', index === active));
      };

      const rebuild = () => {
        const first = items[0].getBoundingClientRect();
        const styles = getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || 0);
        const visible = first.width ? Math.max(1, Math.floor((track.clientWidth + gap) / (first.width + gap))) : 1;
        const pages = Math.max(1, items.length - visible + 1);
        dots.innerHTML = Array.from({ length: pages }, () => '<i></i>').join('');
        dots.hidden = pages <= 1;
        update();
      };

      track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(rebuild).observe(track);
      rebuild();
    });
  }

  function initDescriptionPills(scope = document) {
    qsa('[data-description-pills]', scope).forEach(group => {
      if (group.dataset.descriptionPillsBound === 'true') return;
      group.dataset.descriptionPillsBound = 'true';
      const tabs = qsa('[data-description-tab]', group);
      const panels = qsa('[data-description-panel]', group);
      const activate = tab => {
        const panelID = tab?.getAttribute('aria-controls');
        tabs.forEach(item => { const active = item === tab; item.classList.toggle('is-active', active); item.setAttribute('aria-selected', String(active)); item.tabIndex = active ? 0 : -1; });
        panels.forEach(panel => { panel.hidden = panel.id !== panelID; });
        const activePanel = panels.find(panel => panel.id === panelID);
        if (activePanel?.querySelector('[data-judgeme-pill-target]')) requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let nextIndex = index;
          if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = tabs.length - 1;
          else nextIndex = (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + tabs.length) % tabs.length;
          tabs[nextIndex]?.focus({ preventScroll: true }); activate(tabs[nextIndex]);
        });
      });
      if (tabs.length) activate(tabs.find(tab => tab.classList.contains('is-active')) || tabs[0]);
    });
  }

  function openJudgeMeReviewPill(scroll = false) {
    const target = qs('[data-judgeme-pill-target]');
    const panel = target?.closest('[data-description-panel]');
    const group = panel?.closest('[data-description-pills]');
    const tab = qsa('[data-description-tab]', group || document).find(item => item.getAttribute('aria-controls') === panel?.id);
    if (!tab || !panel) return false;
    tab.click();
    if (scroll) requestAnimationFrame(() => group.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    return true;
  }

  function initJudgeMeReviewPills(scope = document) {
    const targets = qsa('[data-judgeme-pill-target]', scope);
    if (!targets.length) return;
    const moveWidget = () => {
      const widget = qs('#judgeme_product_reviews') || qsa('.jdgm-review-widget, .jdgm-widget').find(element => !element.closest('[data-judgeme-pill-target]'));
      if (!widget) return false;
      const originalSection = widget.closest('.shopify-section');
      targets.forEach((target, index) => {
        if (index > 0 || target.contains(widget)) return;
        target.replaceChildren(widget);
        target.dataset.judgemeIntegrated = 'true';
      });
      if (originalSection && !originalSection.contains(widget)) originalSection.hidden = true;
      window.dispatchEvent(new Event('resize'));
      if (['#reviews', '#judgeme_product_reviews'].includes(location.hash)) openJudgeMeReviewPill(true);
      return true;
    };
    if (moveWidget()) return;
    const observer = new MutationObserver(() => { if (moveWidget()) observer.disconnect(); });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 10000);
  }

  document.addEventListener('click', async event => {
    const toTop = event.target.closest('[data-footer-to-top]');
    if (toTop) {
      window.scrollTo({ top: 0, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return;
    }
    const reviewLink = event.target.closest('a[href="#reviews"],a[href="#judgeme_product_reviews"]');
    if (reviewLink && openJudgeMeReviewPill(true)) event.preventDefault();
    const wishlist = event.target.closest('[data-wishlist-toggle]'); if (wishlist) { event.preventDefault(); Wishlist.toggle(wishlist); }
    const removeWishlist = event.target.closest('[data-wishlist-remove]'); if (removeWishlist) Wishlist.remove(removeWishlist.dataset.wishlistRemove, true);
    const wishlistAdd = event.target.closest('[data-wishlist-add]');
    if (wishlistAdd) {
      const wishlistItem = wishlistAdd.closest('[data-wishlist-item]');
      const variant = qs('[data-wishlist-variant]', wishlistAdd.closest('[data-wishlist-quick-add]'));
      if (!variant?.value) return;
      wishlistAdd.disabled = true;
      wishlistAdd.textContent = 'Adding…';
      try {
        await Cart.add(variant.value, 1, { openDrawer: false });
        if (wishlistItem?.dataset.wishlistItem) Wishlist.remove(wishlistItem.dataset.wishlistItem);
        wishlistNotice('Product moved to cart', { celebrate: true, subtle: true });
      }
      catch (error) { wishlistNotice(error.message); wishlistAdd.disabled = false; wishlistAdd.textContent = 'Add to cart'; }
    }
    const cardQuickTrigger = event.target.closest('[data-card-quick-add-trigger]');
    if (cardQuickTrigger) {
      event.preventDefault();
      QuickAddSheet.open(cardQuickTrigger);
      return;
    }
    const cardQuickClose = event.target.closest('[data-card-quick-add-close]');
    if (cardQuickClose) {
      QuickAddSheet.close();
      return;
    }
    const cardQuickChoice = event.target.closest('[data-card-quick-add-choice]');
    if (cardQuickChoice) {
      QuickAddSheet.choose(cardQuickChoice);
      return;
    }
    const cardQuickSizeGuide = event.target.closest('[data-quick-add-size-guide]');
    if (cardQuickSizeGuide) {
      QuickAddSheet.showSizeGuide(cardQuickSizeGuide);
      return;
    }
    const cardQuickSizeGuideClose = event.target.closest('[data-quick-add-size-guide-close]');
    if (cardQuickSizeGuideClose) {
      QuickAddSheet.hideSizeGuide(cardQuickSizeGuideClose);
      return;
    }
    const cardQuickSubmit = event.target.closest('[data-card-quick-add-submit]');
    if (cardQuickSubmit) {
      await QuickAddSheet.submit(cardQuickSubmit);
      return;
    }
    const quickTrigger = event.target.closest('[data-quick-add-trigger]');
    if (quickTrigger) { const panel = qs('[data-quick-add-panel]', quickTrigger.closest('[data-quick-add]')); const open = panel.hidden; qsa('[data-quick-add-panel]').forEach(item => item.hidden = true); panel.hidden = !open; quickTrigger.setAttribute('aria-expanded', String(open)); }
    const quickClose = event.target.closest('[data-quick-add-close]');
    if (quickClose) { const quickAdd = quickClose.closest('[data-quick-add]'); qs('[data-quick-add-panel]', quickAdd).hidden = true; qs('[data-quick-add-trigger]', quickAdd)?.setAttribute('aria-expanded', 'false'); }
    const quickVariant = event.target.closest('[data-quick-add-variant]');
    if (quickVariant) { quickVariant.disabled = true; try { await Cart.add(quickVariant.dataset.quickAddVariant); } catch (error) { toast(error.message); } finally { quickVariant.disabled = false; } }
    const pairAdd = event.target.closest('[data-pair-add]');
    if (pairAdd) {
      const card = pairAdd.closest('[data-pair-card]'); const panel = qs('[data-pair-sizes]', card); const open = panel?.hidden;
      qsa('[data-pair-sizes]').forEach(item => { if (item !== panel) item.hidden = true; });
      qsa('[data-pair-add]').forEach(item => { if (item !== pairAdd) item.setAttribute('aria-expanded', 'false'); });
      if (panel) panel.hidden = !open; pairAdd.setAttribute('aria-expanded', String(open));
    }
    const pairClose = event.target.closest('[data-pair-close]');
    if (pairClose) {
      const card = pairClose.closest('[data-pair-card]');
      const panel = qs('[data-pair-sizes]', card);
      const trigger = qs('[data-pair-add]', card);
      if (panel) panel.hidden = true;
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus({ preventScroll: true });
    }
    const cartAction = event.target.closest('[data-cart-quantity],[data-cart-remove]');
    if (cartAction) { const line = cartAction.closest('[data-cart-line]'); if (!line) return; const quantity = cartAction.hasAttribute('data-cart-remove') ? 0 : cartAction.dataset.cartQuantity; try { await Cart.change(line.dataset.cartLine, quantity); } catch (error) { toast(error.message); } }
  });

  document.addEventListener('change', event => {
    const selector = event.target.closest('[data-exclusive-variant]');
    if (!selector) return;
    const card = selector.closest('.exclusive-launch__card');
    const addButton = qs('[data-quick-add-variant]', card);
    if (addButton) addButton.dataset.quickAddVariant = selector.value;
  });

  document.addEventListener('change', event => {
    const wishlistVariant = event.target.closest('[data-wishlist-variant]');
    if (!wishlistVariant) return;
    const addButton = qs('[data-wishlist-add]', wishlistVariant.closest('[data-wishlist-quick-add]'));
    if (addButton) addButton.disabled = !wishlistVariant.value;
  });

  qsa('[data-pairs-best]').forEach(section => {
    const track = qs('[data-pairs-track]', section);
    qs('[data-pairs-prev]', section)?.addEventListener('click', () => track?.scrollBy({ left: -track.clientWidth * .8, behavior: 'smooth' }));
    qs('[data-pairs-next]', section)?.addEventListener('click', () => track?.scrollBy({ left: track.clientWidth * .8, behavior: 'smooth' }));
  });

  initPageLoader();

  document.addEventListener('DOMContentLoaded', () => {
    window.themeDrawers = new Drawers();
    Cart.syncCelebrations(document, false);
    Wishlist.updateButtons(); Wishlist.render();
    qsa('[data-product-section]').forEach(section => new ProductPage(section));
    initPredictiveSearch(); initCollection(); initRecommendations(); initHeroSliders(); initScrollSliders(); positionActiveCollectionPills(); initFeaturedCollectionNudges(); initShoppableReels(); initCategoryProducts(); initProductBalance(); initBenefitSliders(); initDescriptionPills(); initJudgeMeReviewPills();

    /* Cart hydration and recently-viewed product requests are non-critical.
       Starting them during initial parsing can keep the browser activity
       indicator alive after the homepage is already visually complete. */
    const hydrateNonCritical = () => {
      Cart.getCached().catch(() => {});
      renderRecentlyViewed();
    };
    if ('requestIdleCallback' in window) window.requestIdleCallback(hydrateNonCritical, { timeout: 1800 });
    else window.setTimeout(hydrateNonCritical, 900);
  });

  document.addEventListener('shopify:section:load', event => {
    initScrollSliders(event.target);
    positionActiveCollectionPills(event.target);
    initCollectionCategorySheet(event.target);
    initCategoryProducts(event.target);
    initProductBalance(event.target);
    initBenefitSliders(event.target);
    initDescriptionPills(event.target);
    initJudgeMeReviewPills(event.target);
  });
})();
